/**
 * Delivery error/reason formatting, ported from Ember's
 * `app/utils/errors/format-error.ts` + its channel formatters.
 *
 * A raw delivery error is often a provider code (`NoDevicesSynced`,
 * `APNS_BadDeviceToken`, `twilio error 30006 …`) that must be rewritten to
 * human copy. Ember dispatches on the delivery's channel (`actionTypeName`):
 * push and SMS/twilio have channel-specific maps; every other channel falls
 * back to the raw detail. We port the two channels that actually reach a
 * delivery's Reason cell — `push` (message map, state-aware, and a multi-device
 * JSON payload reduced to a single error or a device-count summary) and `twilio`
 * (error-code regex) — and fall back to the raw detail otherwise, matching
 * Ember's helper `try/catch` behaviour for unknown channels.
 *
 * SCOPE: Ember's `api`/`stripe` formatters are billing-error maps, not delivery
 * channels, so they're intentionally not ported here. Embedded hyperlinks in a
 * few push messages (and Twilio's error-doc link) are flattened to plain text —
 * the Reason cell renders plain text, so a real anchor is a later enhancement.
 */

import { isEmptyString } from '@/utils/string';

/** The channels with a dedicated formatter — derived from a delivery's `action_type`. */
export type FormatterType = 'push' | 'twilio';

const isKnownFormatter = (type: string): type is FormatterType => type === 'push' || type === 'twilio';

/** Maps a delivery's `action_type` (e.g. `push_action`) to a formatter channel key. */
export function channelFormatterType(actionType: string): FormatterType | undefined {
  const channel = actionType.replace(/_action$/, '');
  return isKnownFormatter(channel) ? channel : undefined;
}

// --- Push -----------------------------------------------------------------

type PushMessage = string | Record<string, string>;
type PushErrorEntry = { code: string; message: PushMessage };
type PushError = string | PushErrorEntry;

const pushError = (provider: string | null, code: string, message: PushMessage): PushErrorEntry => ({
  code: provider != null ? `${provider} ${code}` : code,
  message,
});

// Ported verbatim from Ember `app/utils/errors/messages/push.js`. Two FCM
// entries carry an anchor in Ember; flattened to plain text here.
const PUSH_ERRORS: Record<string, PushError> = {
  NoDevicesSynced: 'No devices synced',
  NoDevice: 'No device found',
  NoProviderConfigured: 'No provider configured for this platform',
  ProviderTokenError: 'Could not generate auth token for provider using supplied credentials',
  EMPTY_NOTIFICATION_BODY:
    'The notification body contained no content. Check the message payload and any Liquid in it.',
  PushInvalidKey: pushError(null, 'Invalid key', 'Check that this is the correct JSON key and try again.'),
  PushInvalidCertificate: pushError(
    null,
    'Invalid certificate',
    'Check that this is the correct .p8 file and try again.',
  ),
  APNS_BadCollapseId: pushError('APNs', '400 BadCollapseId', 'The apns-collapse-id in your custom payload is invalid'),
  APNS_BadDeviceToken: pushError(
    'APNs',
    '400 BadDeviceToken',
    "Device token is invalid or doesn't work with this platform. Register a corrected token.",
  ),
  APNS_BadExpirationDate: pushError(
    'APNs',
    '400 BadExpirationDate',
    'The apns-expiration value in your custom payload is invalid.',
  ),
  APNS_BadMessageId: pushError('APNs', '400 BadMessageId', 'The apns-id value in your custom payload is invalid.'),
  APNS_BadPriority: pushError('APNs', '400 BadPriority', 'The apns-priority value in your custom payload is invalid.'),
  APNS_BadTopic: pushError('APNs', '400 BadTopic', 'Your bundle ID is invalid. Check your configuration.'),
  APNS_DeviceTokenNotForTopic: pushError(
    'APNs',
    '400 DeviceTokenNotForTopic',
    'Device token is not registered with the current bundle ID. Check your configuration or register a corrected token.',
  ),
  APNS_DuplicateHeaders: pushError(
    'APNs',
    '400 DuplicateHeaders',
    'Internal Customer.io Error. Please report to win@customer.io',
  ),
  APNS_IdleTimeout: pushError('APNs', '400 IdleTimeout', {
    default: 'Internal Customer.io Error. Please report to win@customer.io',
    delivery: 'Internal Customer.io Error. Please report to win@customer.io',
  }),
  APNS_MissingDeviceToken: pushError(
    'APNs',
    '400 MissingDeviceToken',
    'Internal Customer.io Error. Please report to win@customer.io',
  ),
  APNS_MissingTopic: pushError(
    'APNs',
    '400 MissingTopic',
    'Internal Customer.io Error. Please report to win@customer.io',
  ),
  APNS_PayloadEmpty: pushError('APNs', '400 PayloadEmpty', 'The message payload is empty.'),
  APNS_TopicDisallowed: pushError(
    'APNs',
    '400 TopicDisallowed',
    'Device token is not registered with the current bundle ID. Check your configuration or register a corrected token.',
  ),
  APNS_BadCertificate: pushError(
    'APNs',
    '403 BadCertificate',
    'Your .p8 certificate is invalid. Check your configuration.',
  ),
  APNS_BadCertificateEnvironment: pushError(
    'APNs',
    '403 BadCertificateEnvironment',
    'Check that your .p8 certificate is for the correct APNs environment.',
  ),
  APNS_ExpiredProviderToken: pushError(
    'APNs',
    '403 ExpiredProviderToken',
    'Internal Customer.io Error. Please report to win@customer.io',
  ),
  APNS_Forbidden: pushError('APNs', '403 Forbidden', 'Internal Customer.io Error. Please report to win@customer.io'),
  APNS_InvalidProviderToken: pushError(
    'APNs',
    '403 InvalidProviderToken',
    'Invalid APNS provider token. Reset the iOS push credentials by disabling and re-enabling push in your workspace settings.',
  ),
  APNS_MissingProviderToken: pushError(
    'APNs',
    '403 MissingProviderToken',
    'Internal Customer.io Error. Please report to win@customer.io',
  ),
  APNS_BadPath: pushError('APNs', '404 BadPath', 'Internal Customer.io Error. Please report to win@customer.io'),
  APNS_MethodNotAllowed: pushError(
    'APNs',
    '405 MethodNotAllowed',
    'Internal Customer.io Error. Please report to win@customer.io',
  ),
  APNS_Unregistered: pushError('APNs', '410 Unregistered', 'This user has uninstalled the application.'),
  APNS_PayloadTooLarge: pushError(
    'APNs',
    '413 PayloadTooLarge',
    'Your message payload was too large. The limit is 4KB.',
  ),
  APNS_TooManyProviderTokenUpdates: pushError('APNs', '429 TooManyProviderTokenUpdates', {
    default: 'Internal Customer.io Error. Please report to win@customer.io',
    delivery: 'Internal Customer.io Error. Please report to win@customer.io',
  }),
  APNS_TooManyRequests: pushError('APNs', '429 TooManyRequests', {
    default: 'Rate limit for device exceeded. We will retry; check back soon.',
    delivery: 'Rate limit for device exceeded. We will retry; check back soon.',
  }),
  APNS_InternalServerError: pushError('APNs', '500 InternalServerError', {
    default: 'Internal Customer.io Error. We will retry; check back soon.',
    delivery: 'Internal Customer.io Error. We will retry; check back soon.',
  }),
  APNS_ServiceUnavailable: pushError('APNs', '503 ServiceUnavailable', {
    default: 'Internal Customer.io Error. We will retry; check back soon.',
    delivery: 'Internal Customer.io Error. We will retry; check back soon.',
  }),
  APNS_Shutdown: pushError('APNs', '503 Shutdown', {
    default: 'Internal Customer.io Error. We will retry; check back soon.',
    delivery: 'Internal Customer.io Error. We will retry; check back soon.',
  }),
  FCM_UNSPECIFIED_ERROR: pushError(
    'FCM',
    'UNSPECIFIED_ERROR',
    'Internal Customer.io Error. Please report to win@customer.io',
  ),
  FCM_INVALID_ARGUMENT: pushError(
    'FCM',
    'INVALID_ARGUMENT',
    'Part of your notification was invalid. Please report this error to win@customer.io for more detail.',
  ),
  FCM_UNREGISTERED: pushError('FCM', 'UNREGISTERED', 'This user has uninstalled the application.'),
  FCM_SENDER_ID_MISMATCH: pushError(
    'FCM',
    'SENDER_ID_MISMATCH',
    'The device token is associated with a different project than your Firebase certificate. Register a corrected token or replace the certificate.',
  ),
  FCM_QUOTA_EXCEEDED: pushError('FCM', 'QUOTA_EXCEEDED', {
    default: 'Rate limit for device exceeded. We will retry; check back soon.',
    delivery: 'Rate limit for device exceeded. We will retry; check back soon.',
  }),
  FCM_APNS_AUTH_ERROR: pushError(
    'FCM',
    'APNS_AUTH_ERROR',
    'Check that your FCM iOS certificate matches the one in Customer.io.',
  ),
  FCM_UNAVAILABLE: pushError('FCM', 'UNAVAILABLE', {
    default: 'Internal Customer.io Error. We will retry; check back soon.',
    delivery: 'Internal Customer.io Error. We will retry; check back soon.',
  }),
  FCM_INTERNAL: pushError('FCM', 'INTERNAL', {
    default: 'Internal Customer.io Error. We will retry; check back soon.',
    delivery: 'Internal Customer.io Error. We will retry; check back soon.',
  }),
  FCM_INVALID_TOKEN: pushError(
    'FCM',
    'INVALID_TOKEN',
    'Invalid platform or token. Retry with another token or platform.',
  ),
  FCM_PERMISSION_DENIED: pushError(
    'FCM',
    'PERMISSION_DENIED',
    'Please make sure that the Firebase Cloud Messaging API is enabled in your Firebase config.',
  ),
};

/** One device's entry in a multi-device push payload (`meta.reason` JSON). */
type DeviceError = Record<string, unknown>;

function pushEntryToString(entry: PushErrorEntry, deliveryState: string | null): string {
  const { message } = entry;
  if (typeof message === 'string') {
    return `${entry.code}: ${message}`;
  }
  // Variant-keyed message; the Reason cell never passes a variant, so use the
  // delivery state's copy when present, else the `default`.
  const variant = deliveryState != null && !isEmptyString(message[deliveryState]) ? deliveryState : 'default';
  return `${entry.code}: ${message[variant] ?? message.default}`;
}

/** The lookup key for a push error is the raw string, or a device error's `detail`. */
function pushErrorKey(error: string | DeviceError): string {
  if (typeof error === 'string') {
    return error;
  }
  const { detail } = error;
  return typeof detail === 'string' ? detail : '';
}

function formatSinglePushError(error: string | DeviceError, deliveryState: string | null): string {
  const key = pushErrorKey(error);
  const configured = PUSH_ERRORS[key];
  if (configured == null) {
    return key;
  }
  return typeof configured === 'string' ? configured : pushEntryToString(configured, deliveryState);
}

/** A push `meta.reason` may be a JSON array of per-device statuses. */
function parseDeviceErrors(reason: string): DeviceError[] | null {
  try {
    const parsed: unknown = JSON.parse(reason);
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return Array.isArray(parsed) ? (parsed as DeviceError[]) : null;
  } catch {
    return null;
  }
}

// Reduce a multi-device payload to a single error when possible — a lone entry,
// or the single entry matching the parent delivery state — else the caller shows
// a device-count summary. Ports Ember's `reduceToSingleErrorIfPossible`.
function reduceToSingleError(errors: DeviceError[], deliveryState: string | null): DeviceError | undefined {
  if (errors.length === 1) {
    return errors[0];
  }
  const matching = errors.filter((error) => {
    if (deliveryState === 'failed') {
      return error.status !== 200;
    }
    if (deliveryState == null) {
      return false;
    }
    return Boolean(error[deliveryState]);
  });
  return matching.length === 1 ? matching[0] : undefined;
}

function pushFormatter(reason: string, deliveryState: string | null): string {
  const devices = parseDeviceErrors(reason);
  if (devices != null) {
    const single = reduceToSingleError(devices, deliveryState);
    if (single != null) {
      return formatSinglePushError(single, deliveryState);
    }
    return `${devices.length} devices ${deliveryState ?? ''}. See the Delivery Details page for more info.`;
  }
  return formatSinglePushError(reason, deliveryState);
}

// --- Twilio ---------------------------------------------------------------

// Twilio surfaces an error code either inline (immediate send failure) or via a
// later bounce notification. Ember linked the code to Twilio's docs; we keep the
// readable code line as plain text (the anchor is a later enhancement).
const TWILIO_REGEXES = [/(twilio error )(\d+)(.*)/i, /(twilio error code: )(\d+)(.*)/i];

function twilioFormatter(errorKey: string): string {
  const matched = TWILIO_REGEXES.find((regex) => regex.test(errorKey));
  if (matched == null) {
    return errorKey;
  }
  const [, prefix, code, suffix] = errorKey.match(matched) ?? [];
  return `${prefix ?? ''}${code ?? ''}${suffix ?? ''}`;
}

// --- Dispatch -------------------------------------------------------------

/** Options mirroring Ember's `formatError(error, type, variant, deliveryState)`. */
export interface FormatErrorOptions {
  /** Formatter channel — from `channelFormatterType(action_type)`. */
  type?: FormatterType | null;
  /** Parent delivery state, used by the push variant map. */
  deliveryState?: string | null;
}

/** Pull the raw human string out of an error value for the fallback path. */
function extractDetail(error: string | Record<string, unknown> | null | undefined): string {
  if (typeof error === 'string') {
    return error;
  }
  if (error != null && typeof error === 'object' && 'detail' in error) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    const { detail } = error as { detail: unknown };
    return typeof detail === 'string' ? detail : 'Unknown error';
  }
  return 'Unknown error';
}

/**
 * Resolve a delivery error/reason to human copy. Dispatches to the channel
 * formatter named by `type`; falls back to the raw detail for unknown channels
 * (email/webhook/slack/etc.), matching Ember's ungated helper.
 */
export function formatError(
  error: string | Record<string, unknown> | null | undefined,
  { type = null, deliveryState = null }: FormatErrorOptions = {},
): string {
  const detail = extractDetail(error);
  if (type == null || !isKnownFormatter(type)) {
    return detail;
  }
  if (type === 'twilio') {
    return twilioFormatter(detail);
  }
  return pushFormatter(detail, deliveryState);
}
